import axios from "../axios";
import { CircularProgress } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import {
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import Login from "../login/Login";
import Home from "../components/home/Home";
import LandingPage from "../components/landing/LandingPage";
import PlatformBuilder from "../components/platform-builder/PlatformBuilder";
import AskQuestion from "../components/question/AskQuestion";
import { ADD_NOTIFICATION, LOGIN, SET_ACCOUNTS, SET_FACILITIES } from "../store/types";
import { useDispatch, useSelector } from "react-redux";
import ResetPassword from "../components/reset-password/ResetPassword";
import SettingsPage from "../components/settings/Settings";
import Logs from "../components/logs/Logs";
import TutorialsPage from "../pages/TutorialsPage";
import AccountFacilities from "../pages/settings/Facilities";
import DataInput from "../components/new-data-input/DataInput";
import ResidenceList from "../pages/ResidenceList";
import Dashboard from "../components/dashboard/Dashboard";
import HospitalPage from "../pages/Hospital";
import AccountsPage from "../pages/settings/AccountsPage";
import {
    setFacilityAccess,
    setRoleName,
} from "../store/reducers/permission.slice";
import { store } from "../index";
import { setFacilityId } from "../utilis/facilityStorage";
import _ from "lodash";
import AdmissionPage from "../pages/Admission";
import CommunityTransferPage from "../pages/CommunityTransfer";
import DeceasedPage from "../pages/Deceased";
import OverallPage from "../pages/Overall";
// import QuickGlacePage from "../pages/QuickGlace";
import AccountUserPage from "../pages/settings/UsersPage";
import { AUTH_ROLES } from "../types/auth.type";
import ManageProfilePage from "../pages/settings/ManageProfilePage";
import AuditLogsPage from "../pages/settings/AuditLogsPage";
import ForgotPassword from "../login/forgot-password/ForgotPassword";
import { setFacilityLoaded, setIsFacilityLoading, setSelectedAccount, setIsLoginLoading } from "../store/reducers/common.slice";
import useSetParamToURL from "../components/hooks/useSetParamToURL";
import VersionPage from "../version/version";
import { getDashboardPathByAccess } from "../utilis/common";
import { useMediaQuery } from "@mui/system";
import { signOut } from "../utilis/signout";
import PopupDialog from "../components/popupDialog/PopupDialog";

import { checkBackendHealth } from "../utilis/healthCheck";
import MaintenancePage from "../components/MaintenancePage";
import useAccountId from "../hooks/useAccountId";
import useFacilityId from "../hooks/useFacilityId";

const AppRouter = (props) => {
    const auth = useSelector((state) => state.auth);
    const account = useSelector((state) => state.account);
    const facility = useSelector((state) => state.facility);
    const permission = useSelector((state) => state.permission);
    const { selectedAccount, isLoginLoading } = useSelector((state) => state.common);

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { jwt } = props;
    const { setParamToURL, setPathWithParams } = useSetParamToURL();
    const [searchParams] = useSearchParams();
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [showNoReadPermissionPopup, setShowNoReadPermissionPopup] = useState(false);
    const [serverDown, setServerDown] = useState(false);
    const [checkingServer, setCheckingServer] = useState(true);
    const signOutCalledRef = useRef(false);
    const { accountId: accountIdFromHook } = useAccountId();
    const { facilityId: facilityIdFromHook } = useFacilityId();
    const redirectAfter = searchParams.get("redirectAfter");

    const disableDefaultRedirect = !!redirectAfter;

    const accountId = useMemo(() => {
        // Guard: Only run this logic if user is authenticated and not on login page
        // This prevents repopulating state after logout
        // Key check: auth object must have data (after LOGOUT it becomes empty {})
        const hasAuthData = auth && Object.keys(auth).length > 0;
        const isAuthenticated = jwt && hasAuthData;
        const isLoginPage = location.pathname === "/login" || location.pathname === "/";

        // Skip if not authenticated or on login page
        // This prevents repopulating Redux state and localStorage after logout
        // Even if jwt exists or localStorage has data, if auth is empty (after LOGOUT),
        // this logic won't run, preventing state repopulation
        if (!isAuthenticated || isLoginPage) {
            return null;
        }

        const selectedAccountId = selectedAccount?._id || searchParams.get("accountId") || accountIdFromHook;
        if (selectedAccountId) {
            // Update Redux state first (tab-specific), then localStorage for persistence (multi-tab safe)
            // Redux state is the source of truth, localStorage is just for persistence
            if (!selectedAccount || selectedAccount._id !== selectedAccountId) {
                // Only update if not already set in Redux
                // Ensure account is an array before using .find()
                const accountToSet = Array.isArray(account) && account.find(acc => acc._id === selectedAccountId);
                if (accountToSet) {
                    dispatch(setSelectedAccount(accountToSet));
                }
            }
            sessionStorage.setItem("accountId", selectedAccountId);
        }

        return selectedAccountId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount, searchParams.get("accountId"), account, dispatch, jwt, auth, location.pathname, accountIdFromHook]);

    const getUser = async () => {
        let re = await axios.get("/api/user").catch((e) => e);
        if (re?.response?.status === 403 || re?.response?.status === 500) {
            localStorage.removeItem("x-auth");
            return navigate("/login");
        }
        const { user = null } = re.data;
        if (user) {
            await dispatch({ type: LOGIN, payload: user });
            await dispatch(setRoleName(user?.role?.slug));
        } else {
            return navigate("/login");
        }
    };

    const getAccount = async () => {
        let re = await axios.get("/api/account", {
            params: { withFacilities: permission && permission.role === AUTH_ROLES.USER && isMobile ? true : false }
        }).catch(async (e) => {
            console.error(e);
            dispatch(setIsLoginLoading(false));
            return navigate("/login");
        });

        if (!re?.data || re.data.length === 0) {
            dispatch(setIsLoginLoading(false));
            return navigate("/login");
        }

        if (re?.response?.status === 403) {
            localStorage.removeItem("x-auth");
            return navigate("/login");
        }
        const accountsData = _.orderBy(re.data, ["name"], ["desc"]);
        // Global check: Check all accounts' facilities for read permission
        if (permission && permission.role === AUTH_ROLES.USER && isMobile) {
            let allFacilitiesFromAllAccounts = [];
            let accountsWithReadAccess = [];

            // Collect facilities from all accounts and check read access
            const accountItems = accountsData;
            for (const accountItem of accountItems) {
                const facilities = accountItem.facilities;
                if (facilities?.length > 0) {
                    allFacilitiesFromAllAccounts = allFacilitiesFromAllAccounts.concat(facilities);

                    // Check if this account has at least one facility with read access
                    const hasReadAccess = facilities.some(facility => facility.read === true);
                    if (hasReadAccess) {
                        accountsWithReadAccess.push(accountItem);
                    }
                }
            }
            // Check if ALL facilities from ALL accounts have no read permission
            if (allFacilitiesFromAllAccounts.length > 0) {
                const allFacilitiesNoRead = allFacilitiesFromAllAccounts.every(facility => facility.read !== true);

                if (allFacilitiesNoRead) {
                    // All facilities from all accounts have no read permission, show message and logout
                    dispatch({
                        type: ADD_NOTIFICATION,
                        payload: {
                            type: "error",
                            label: "You don't have access to this account in mobile.",
                            id: "noReadPermissionError",
                        },
                    });
                    signOut(navigate);
                    return;
                }
            }
            // Only dispatch accounts that have at least one facility with read access
            await dispatch({ type: SET_ACCOUNTS, payload: accountsWithReadAccess });
        } else {
            await dispatch({ type: SET_ACCOUNTS, payload: re.data });
        }
        if ((!accountId || accountId === null || accountId === '' || accountId === 'null') && !isMobile) {
            let accountIdFromRedirect = null;

            if (redirectAfter) {
                try {
                    const decodedRedirect = decodeURIComponent(redirectAfter);
                    const query = decodedRedirect.split("?")[1];
                    if (query) {
                        const paramsFromRedirect = new URLSearchParams(query);
                        accountIdFromRedirect = paramsFromRedirect.get("accountId");
                    }
                } catch (err) {
                    console.warn("Failed to parse redirectAfter:", redirectAfter, err);
                }
            }

            const matchedAccount =
                accountIdFromRedirect &&
                re.data.find((acc) => acc._id === accountIdFromRedirect);

            const fallbackAccount = re.data[0];
            const finalAccount = matchedAccount || fallbackAccount;

            setParamToURL([{ key: "accountId", value: finalAccount._id }]);
            // Update Redux state first (tab-specific), then sessionStorage for persistence (multi-tab safe)
            // Redux state is the source of truth, sessionStorage is just for persistence
            dispatch(setSelectedAccount(finalAccount));
            sessionStorage.setItem("accountId", finalAccount._id);
        }
    };

    // Helper function to get appropriate default redirect based on permissions
    const getDefaultRedirectPath = () => {
        if (!permission || !selectedAccount) return "/dashboard/hospital";

        if (permission.role === AUTH_ROLES.USER) {
            const facilityAccess = permission.facilityAccess;
            if (!facilityAccess) return "/dashboard/hospital";

            const hasRead = facilityAccess.read;
            const hasWrite = facilityAccess.write;

            if (hasRead && hasWrite) {
                // Read + Write: Check dashboard access
                if (selectedAccount?.dashboardAccess?.length > 0) {
                    return getDashboardPathByAccess(selectedAccount);
                }
                return "/data-input";
            } else if (hasWrite && !hasRead) {
                // Write only: data-input for desktop, never for mobile
                return isMobile ? "/dashboard/hospital" : "/data-input";
            } else if (hasRead && !hasWrite) {
                // Read only: dashboard only
                if (selectedAccount?.dashboardAccess?.length > 0) {
                    return getDashboardPathByAccess(selectedAccount);
                }
                return "/dashboard/hospital";
            }
        } else {
            // Non-USER roles: dashboard based on account access
            if (selectedAccount?.dashboardAccess?.length > 0) {
                return getDashboardPathByAccess(selectedAccount);
            }
            return "/dashboard/hospital";
        }

        return "/dashboard/hospital";
    };

    // Helper function to check if user can access data-input
    const canAccessDataInput = () => {
        if (!permission) return false;

        if (permission.role === AUTH_ROLES.USER) {
            const facilityAccess = permission.facilityAccess;
            if (!facilityAccess) return false;

            // Only allow data-input access if user has write permission and not on mobile
            return facilityAccess.write && !isMobile;
        } else {
            // Non-USER roles can access data-input if not on mobile
            return !isMobile;
        }
    };

    // Helper function to check if current path is a valid authenticated route
    const isValidAuthenticatedRoute = (pathname) => {
        const validRoutes = [
            "/dashboard",
            "/data-input",
            "/residence-list",
            "/logs",
            "/tutorials",
            "/settings"
        ];
        return validRoutes.some(route => pathname.startsWith(route));
    };

    const getFacilities = async (action = "get") => {
        dispatch(setIsFacilityLoading(true));
        let storedAccountId = accountId;
        if (!storedAccountId) {
            storedAccountId = account[0]._id;
        }

        let re2 = await axios
            .get(`/api/facility/list/${storedAccountId}`)
            .catch((e) => e)
            .finally(() => {
                dispatch(setIsLoginLoading(false));
            });
        if (re2?.response?.status === 403) {
            localStorage.removeItem("x-auth");
            return navigate("/login");
        }

        if (re2.data && re2.data.length > 0) {
            await dispatch(setFacilityLoaded(false));
        } else {
            await dispatch(setFacilityLoaded(true));
        }
        if (permission && permission.role === AUTH_ROLES.USER && re2.data.length > 0 && isMobile) {
            const facilitiesWithReadPermission = re2.data.filter(facility => facility.read === true);
            await dispatch({ type: SET_FACILITIES, payload: facilitiesWithReadPermission });
            dispatch(setIsFacilityLoading(false));
        } else {
            await dispatch({ type: SET_FACILITIES, payload: re2.data });
            dispatch(setIsFacilityLoading(false));
        }
        // Get facilityId from Redux state (tab-specific), fallback to localStorage
        const state = store.getState();
        const activeFacilities = state?.activeFacilities?.facilities;
        let storedFacilityId = (activeFacilities && activeFacilities.length > 0)
            ? activeFacilities[0]
            : facilityIdFromHook || sessionStorage.getItem("facilityId");
        if (re2.data.length === 0) return;
        if (!storedFacilityId && !isMobile) {
            storedFacilityId = _.orderBy(re2.data, "name", "ASC")?.[0]?._id || re2.data[0]._id;
            // Use utility function to update Redux and sync localStorage (multi-tab safe)
            setFacilityId(storedFacilityId, { updateLocalStorage: true });
        }

        // Skip redirect if user is already on a valid authenticated route
        const shouldSkipRedirect = isValidAuthenticatedRoute(location.pathname);

        if (permission && permission.role === AUTH_ROLES.USER) {
            let facilityIdUpdated = storedFacilityId;
            if (re2.data.length > 0 && _.find(re2.data, { _id: facilityIdUpdated }) === undefined) {
                facilityIdUpdated = re2.data[0]?._id;
            }
            const selectedFacilityItem = _.find(re2.data, { _id: facilityIdUpdated });
            if (selectedFacilityItem) {
                await dispatch(
                    setFacilityAccess({
                        read: selectedFacilityItem.read,
                        write: selectedFacilityItem.write,
                    })
                );

                const queryParams = [
                    { key: "accountId", value: storedAccountId },
                    { key: "facilityId", value: selectedFacilityItem._id }
                ];

                // Skip redirect if user is already on a valid route and facilities are just being refreshed
                if (shouldSkipRedirect) {
                    // Only update URL params without changing the route
                    setParamToURL(queryParams);
                    return;
                }

                // Handle different permission combinations
                const hasRead = selectedFacilityItem.read;
                const hasWrite = selectedFacilityItem.write;
                if (redirectAfter) {
                    window.location.href = redirectAfter;
                } else if (hasRead && hasWrite) {
                    // Read + Write permissions: Check account dashboard access and redirect accordingly
                    if (selectedAccount?.dashboardAccess?.length > 0) {
                        const targetDashboardPath = getDashboardPathByAccess(selectedAccount);
                        setPathWithParams(targetDashboardPath, queryParams);
                    } else {
                        // Default to data-input if no specific dashboard access
                        setPathWithParams("/data-input", queryParams);
                    }
                } else if (hasWrite && !hasRead) {
                    setPathWithParams("/data-input", queryParams);
                } else if (hasRead && !hasWrite) {
                    if (selectedAccount?.dashboardAccess?.length > 0) {
                        const targetDashboardPath = getDashboardPathByAccess(selectedAccount);
                        setPathWithParams(targetDashboardPath, queryParams);
                    } else {
                        setPathWithParams("/dashboard/hospital", queryParams);
                    }
                } else {
                    // No permissions - this shouldn't happen, but handle gracefully
                    dispatch({
                        type: ADD_NOTIFICATION,
                        payload: {
                            type: "error",
                            label: "You don't have access to this facility.",
                            id: "noAccessError",
                        },
                    });
                    signOut(navigate);
                    return;
                }
            }
        } else {
            const accountItem = selectedAccount ? selectedAccount : account[0];
            const queryParams = [{ key: "accountId", value: storedAccountId }, { key: "facilityId", value: storedFacilityId }];

            // Skip redirect if user is already on a valid route and facilities are just being refreshed
            if (shouldSkipRedirect) {
                // Only update URL params without changing the route
                setParamToURL(queryParams);
                return;
            }

            if (redirectAfter) {
                window.location.href = redirectAfter;
            } else if (accountItem?.dashboardAccess?.length > 0) {
                const targetDashboardPath = getDashboardPathByAccess(accountItem);
                setPathWithParams(targetDashboardPath, queryParams);
            } else {
                setPathWithParams("/dashboard/hospital", queryParams);
            }
        }
    };

    useEffect(() => {
        const verifyBackend = async () => {
            setCheckingServer(true);
            const isHealthy = await checkBackendHealth();
            setServerDown(!isHealthy);
            setCheckingServer(false);
        };

        verifyBackend();
    }, []);

    useEffect(() => {
        if (!account || !Array.isArray(account) || !account?.length) return;
        getFacilities();
        // eslint-disable-next-line
    }, [account]);

    useEffect(() => {
        const fetchData = async () => {
            if (!(account === "refreshing" || facility === "refreshing")) return;

            if (account === "refreshing") {
                await getAccount();
            }
            if (account !== "refreshing" && facility === "refreshing") {
                await getFacilities(facility);
            }
        };
        fetchData();
        // eslint-disable-next-line
    }, [account, facility]);

    useEffect(() => {
        if (!jwt) {
            // Prevent infinite loop - only call signOut once and only if not already on login/landing page
            if (signOutCalledRef.current) return;

            // If JWT is missing on page refresh/reload, check if there's auth data in localStorage
            // If auth data exists, call signOut to properly clean up localStorage
            const hasAuthData = localStorage.getItem("x-auth") ||
                accountIdFromHook ||
                sessionStorage.getItem("facilityId") ||
                localStorage.getItem("facilityId");

            // Only call signOut if:
            // 1. Auth data exists in localStorage
            // 2. We're not already on login or landing page (to avoid unnecessary signOut calls)
            // 3. signOut hasn't been called yet in this session
            if (hasAuthData &&
                location.pathname !== "/login" &&
                location.pathname !== "/" &&
                location.pathname !== "/build-platform" &&
                location.pathname !== "/ask-question") {
                signOutCalledRef.current = true;
                // JWT is missing but auth data exists - user was logged in before refresh
                // Call signOut to clean up all localStorage and session data
                signOut(navigate);
            }
            return;
        }
        getUser();
        getAccount();
        // eslint-disable-next-line
    }, []);

    if (checkingServer) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "90vh" }}>
                <CircularProgress />
            </div>
        );
    }

    if (serverDown) {
        return <MaintenancePage />;
    }

    if (isLoginLoading) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "90vh",
                }}
            >
                <CircularProgress />
            </div>
        );
    }

    return (
        <>
            <Routes>
                {/* Landing Page Routes - Only show when not authenticated */}
                <Route path="/" element={!jwt ? <LandingPage /> : <Navigate to={getDefaultRedirectPath()} />} />
                <Route path="/build-platform" element={!jwt ? <PlatformBuilder /> : <Navigate to={getDefaultRedirectPath()} />} />
                <Route path="/ask-question" element={!jwt ? <AskQuestion /> : <Navigate to={getDefaultRedirectPath()} />} />

                {/* Authentication Routes */}
                <Route path="/login" element={jwt ? <Navigate to={getDefaultRedirectPath()} /> : <Login jwt={jwt} />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/version" element={<VersionPage />} />
                <Route path="/new-password/:token?" element={<ResetPassword />} />

                {jwt ? (
                    <Route path="/reset-password" element={<ResetPassword />} />
                ) : undefined}

                {/* Redirect to login if not authenticated */}
                {(location.pathname !== "/login" && location.pathname !== "/new-password" && location.pathname !== "/" && location.pathname !== "/build-platform" && location.pathname !== "/ask-question") && !jwt ? (
                    <Route path="/*" element={<Navigate to="/" />} />
                ) : undefined}

                {/* Force password reset if required */}
                {auth?.requirePasswordReset ? (
                    <Route path="/*" element={<Navigate to="/reset-password" />} />
                ) : undefined}

                {/* Authenticated Routes */}
                {jwt && !auth?.requirePasswordReset ? (
                    <>
                        {account !== null && facility !== null ? (
                            <>
                                {/* Data Input Route - Protected by write permission */}
                                <Route
                                    path="/data-input"
                                    element={canAccessDataInput() ? <Home /> : <Navigate to={getDefaultRedirectPath()} />}
                                >
                                    <Route index element={<div className="ph-no-capture"><DataInput /></div>} />
                                </Route>

                                {/* Residents List Route */}
                                <Route path="/residence-list" element={<Home />}>
                                    <Route index element={<ResidenceList />} />
                                </Route>

                                {/* Dashboard Routes */}
                                <Route path="/dashboard" element={<Home />}>
                                    <Route path="" element={<Dashboard />}>
                                        <Route path="hospital" element={<HospitalPage />} />
                                        <Route path="admissions" element={<AdmissionPage />} />
                                        <Route path="community-transfer" element={<CommunityTransferPage />} />
                                        <Route path="deceased" element={<DeceasedPage />} />
                                        <Route path="overall" element={<OverallPage />} />
                                        {/* <Route path="quick-glace" element={<QuickGlacePage />} /> */}
                                    </Route>
                                </Route>

                                {/* Logs Route */}
                                <Route path="/logs" element={<Home />}>
                                    <Route index element={<Logs />} />
                                </Route>

                                {/* Tutorials Route */}
                                <Route path="/tutorials" element={<Home />}>
                                    <Route index element={<TutorialsPage />} />
                                </Route>

                                {/* Settings Routes */}
                                <Route path="/settings" element={<Home />}>
                                    <Route path="" element={<SettingsPage />}>
                                        <Route path="profile" element={<ManageProfilePage />} />
                                        <Route path="users" element={<AccountUserPage />} />
                                        <Route path="accounts" element={<AccountsPage />} />
                                        <Route path="facilities" element={<AccountFacilities />} />
                                        <Route path="security/audit-logs" element={<AuditLogsPage />} />
                                    </Route>
                                </Route>
                            </>
                        ) : undefined}
                    </>
                ) : undefined}

                {/* Default redirect */}
                {!disableDefaultRedirect && (
                    <Route path="*" element={<Navigate to={jwt ? getDefaultRedirectPath() : "/"} />} />
                )}
            </Routes>

            {/* Popup for mobile users with no read permission */}
            <PopupDialog
                state={showNoReadPermissionPopup}
                onClose={() => setShowNoReadPermissionPopup(false)}
                onConfirm={() => setShowNoReadPermissionPopup(false)}
                dialogMessage="You can't choose this account in mobile. Please use desktop to access this account."
                confirmBtnText="Close"
            />
        </>
    );
};

export default AppRouter;

